import React from 'react'
import '../styles/animations.css'; // Ensure animations are available
import './SkeletonLoader.css';

/**
 * Modern animated skeleton loader component
 * Uses shimmer animation for a polished loading experience
 */

// Base skeleton element with shimmer animation
function SkeletonElement({ width, height, borderRadius, className = '', style = {} }) {
    return (
        <div
            className={`skeleton ${className}`}
            style={{
                width: width || '100%',
                height: height || '1rem',
                borderRadius: borderRadius || 'var(--radius-sm)',
                ...style
            }}
        />
    )
}

// Skeleton for poll choice items
function SkeletonChoice({ hasPrice = false }) {
    return (
        <div className="skeleton-choice-item">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <SkeletonElement width="60%" height="1.125rem" />
                {hasPrice && <SkeletonElement width="4rem" height="1rem" />}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <SkeletonElement width="100%" height="0.5rem" borderRadius="var(--radius-lg)" />
                <SkeletonElement width="3rem" height="0.875rem" />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <SkeletonElement width="2rem" height="2rem" borderRadius="50%" />
                <SkeletonElement width="2rem" height="2rem" borderRadius="50%" />
            </div>
        </div>
    )
}

// Full poll page skeleton - matches actual PollHeader, ChoiceList, AddChoiceForm layout
function PollPageSkeleton() {
    return (
        <div className="poll-container fade-in">
            {/* PollHeader skeleton */}
            <div className="card fade-in-up" style={{ padding: '1rem 1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Title Row: Icon + Title */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                        <SkeletonElement width="2rem" height="2rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="60%" height="1.75rem" style={{ marginTop: '0.125rem' }} />
                    </div>

                    {/* Bottom Row: Code badge + Live status + Buttons */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        paddingTop: '0.5rem',
                        borderTop: '1px solid var(--border-color)'
                    }}>
                        {/* Left: Code badge + Live indicator */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                            <SkeletonElement width="6rem" height="2.25rem" borderRadius="var(--radius-md)" />
                            <SkeletonElement width="3.5rem" height="1.5rem" borderRadius="var(--radius-lg)" />
                        </div>
                        {/* Right: Edit + Share buttons */}
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <SkeletonElement width="2.5rem" height="2.25rem" borderRadius="var(--radius-md)" />
                            <SkeletonElement width="2.5rem" height="2.25rem" borderRadius="var(--radius-md)" />
                        </div>
                    </div>
                </div>
            </div>

            {/* ChoiceList skeleton - Results section */}
            <div className="card fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
                {/* Results header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <SkeletonElement width="1.5rem" height="1.5rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="5rem" height="1.25rem" />
                    </div>
                    <SkeletonElement width="2.5rem" height="2.25rem" borderRadius="var(--radius-md)" />
                </div>

                {/* Empty state placeholder (matching "No choices yet" message) */}
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <SkeletonElement width="16rem" height="1.125rem" style={{ margin: '0 auto' }} />
                </div>
            </div>

            {/* AddChoiceForm skeleton */}
            <div className="card fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                {/* Add a Choice header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <SkeletonElement width="1rem" height="1rem" borderRadius="var(--radius-sm)" />
                    <SkeletonElement width="7rem" height="1.25rem" />
                </div>

                {/* Input field */}
                <SkeletonElement height="3rem" style={{ marginBottom: '0.75rem' }} />

                {/* Price/options toggle */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
                    <SkeletonElement width="10rem" height="1.5rem" borderRadius="var(--radius-lg)" />
                </div>

                {/* Add Choice button */}
                <SkeletonElement height="2.75rem" borderRadius="var(--radius-md)" />
            </div>
        </div>
    )
}

// Review page skeleton
function ReviewPageSkeleton() {
    return (
        <div className="review-skeleton fade-in">
            {/* Header card */}
            <div className="card skeleton-header-card" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <SkeletonElement width="1.5rem" height="1.5rem" borderRadius="50%" />
                    <SkeletonElement width="12rem" height="1.5rem" />
                </div>
                <SkeletonElement width="100%" height="1rem" style={{ marginBottom: '0.5rem' }} />
                <SkeletonElement width="80%" height="1rem" />
            </div>

            {/* Title section */}
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
                <SkeletonElement width="6rem" height="1rem" style={{ marginBottom: '1rem' }} />
                <SkeletonElement height="3.5rem" />
            </div>

            {/* Currency section */}
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
                <SkeletonElement width="10rem" height="1rem" style={{ marginBottom: '1rem' }} />
                <SkeletonElement width="8rem" height="2.5rem" />
            </div>

            {/* Choices section */}
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
                <SkeletonElement width="6rem" height="1rem" style={{ marginBottom: '1rem' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div
                            key={i}
                            className="skeleton-review-choice"
                            style={{
                                padding: '1rem',
                                border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-md)',
                                animationDelay: `${i * 0.08}s`
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                                <SkeletonElement width="70%" height="1.125rem" />
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <SkeletonElement width="2rem" height="2rem" borderRadius="var(--radius-md)" />
                                    <SkeletonElement width="2rem" height="2rem" borderRadius="var(--radius-md)" />
                                </div>
                            </div>
                            <SkeletonElement width="5rem" height="1.5rem" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Confirm button */}
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <SkeletonElement height="3rem" />
            </div>
        </div>
    )
}

// Admin page skeleton - matches actual two-column layout
function AdminPageSkeleton() {
    return (
        <div className="admin-container fade-in" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
            {/* Header Card - Admin Dashboard title + Logout */}
            <div className="card fade-in-up" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <SkeletonElement width="1.75rem" height="1.75rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="12rem" height="1.5rem" />
                    </div>
                    <SkeletonElement width="5rem" height="2.25rem" borderRadius="var(--radius-md)" />
                </div>
                <SkeletonElement width="22rem" height="0.875rem" style={{ marginTop: '0.5rem' }} />
            </div>

            {/* Two-column layout: AI Provider + Video Management */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>

                {/* Left: AI Provider Configuration */}
                <div className="card fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <SkeletonElement width="1.25rem" height="1.25rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="10rem" height="1.25rem" />
                    </div>
                    <SkeletonElement width="16rem" height="0.75rem" style={{ marginBottom: '1rem' }} />

                    {/* Provider list with radio buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                                <SkeletonElement width="1rem" height="1rem" borderRadius="50%" />
                                <div style={{ flex: 1 }}>
                                    <SkeletonElement width="6rem" height="0.875rem" style={{ marginBottom: '0.25rem' }} />
                                    <SkeletonElement width="12rem" height="0.625rem" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Models section */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <SkeletonElement width="1rem" height="1rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="5rem" height="1rem" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {[1, 2].map((i) => (
                            <SkeletonElement key={i} height="2.5rem" borderRadius="var(--radius-md)" />
                        ))}
                    </div>
                </div>

                {/* Right: Video Management */}
                <div className="card fade-in-up" style={{ animationDelay: '0.15s', animationFillMode: 'both' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                        <SkeletonElement width="1.25rem" height="1.25rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="9rem" height="1.25rem" />
                    </div>

                    {/* Upload section */}
                    <SkeletonElement width="7rem" height="0.875rem" style={{ marginBottom: '0.5rem' }} />
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <SkeletonElement height="2.25rem" style={{ flex: 1 }} borderRadius="var(--radius-md)" />
                        <SkeletonElement width="5rem" height="2.25rem" borderRadius="var(--radius-md)" />
                    </div>
                    <SkeletonElement width="14rem" height="0.625rem" style={{ marginBottom: '1rem' }} />

                    {/* Library header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <SkeletonElement width="1rem" height="1rem" borderRadius="var(--radius-sm)" />
                        <SkeletonElement width="4rem" height="0.875rem" />
                    </div>

                    {/* Video thumbnails */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                        {[1, 2, 3].map((i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                                <SkeletonElement width="5rem" height="2.75rem" borderRadius="var(--radius-sm)" />
                                <div style={{ flex: 1, display: 'flex', gap: '0.5rem' }}>
                                    <SkeletonElement width="2rem" height="1rem" borderRadius="var(--radius-sm)" />
                                    <SkeletonElement width="3rem" height="1rem" />
                                    <SkeletonElement width="2rem" height="1rem" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Delete all button */}
                    <SkeletonElement height="2.25rem" borderRadius="var(--radius-md)" />
                </div>
            </div>

            {/* Database Maintenance section */}
            <div className="card fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <SkeletonElement width="1.25rem" height="1.25rem" borderRadius="var(--radius-sm)" />
                    <SkeletonElement width="11rem" height="1.25rem" />
                </div>
                <SkeletonElement width="100%" height="0.875rem" style={{ marginBottom: '0.25rem' }} />
                <SkeletonElement width="70%" height="0.875rem" style={{ marginBottom: '1rem' }} />
                <SkeletonElement height="2.5rem" borderRadius="var(--radius-md)" />
            </div>
        </div>
    )
}

// Dashboard skeleton - matches actual Dashboard layout
function DashboardSkeleton() {
    return (
        <div className="dashboard-skeleton fade-in">
            {/* Activity Ticker Skeleton */}
            <div style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'var(--bg-primary)',
                borderBottom: '1px solid var(--border-color)',
                padding: '0.75rem 1rem',
                marginBottom: '2rem'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
                    <SkeletonElement width="1.5rem" height="1.5rem" borderRadius="var(--radius-sm)" />
                    <SkeletonElement width="20rem" height="1rem" />
                </div>
            </div>

            <div className="dashboard-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
                {/* Header */}
                <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <SkeletonElement width="14rem" height="2.5rem" style={{ marginBottom: '0.5rem' }} />
                    <SkeletonElement width="20rem" height="1.25rem" />
                </div>

                {/* Stat Cards */}
                <div className="dashboard-stats" style={{
                    marginBottom: '3rem'
                    // gap and display handled by .dashboard-stats class check to be sure,
                    // actually .dashboard-stats has display: flex, gap: var(--space-md), justify-content: center
                    // We should rely on the class as much as possible, but let's check if dashboard-stats is globally available or imported in Dashboard.css
                    // It is in Dashboard.css which is imported in Dashboard.js, but SkeletonLoader.js imports SkeletonLoader.css.
                    // However, we can duplicate the important layout styles inline to be safe and match visually without relying on external CSS presence if possible,
                    // OR better, since DashboardSkeleton is used inside Dashboard.js where Dashboard.css IS loaded, we can just use the class name!
                    // Let's use the className and minimal inline overrides.
                }}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="dashboard-stat-card skeleton-stat-card" style={{
                            background: 'var(--bg-secondary)',
                            // padding/borderRadius/gap handled by CSS class .dashboard-stat-card
                            border: '1px solid var(--border-color)',
                            minWidth: '180px', // Ensure they aren't too small
                        }}>
                            <SkeletonElement width="40px" height="40px" borderRadius="var(--radius-md)" />
                            <div style={{ flex: 1 }}>
                                <SkeletonElement width="3rem" height="1.5rem" style={{ marginBottom: '0.25rem' }} />
                                <SkeletonElement width="4rem" height="0.8rem" />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Polls Section Header */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <SkeletonElement width="10rem" height="2rem" style={{ marginBottom: '1rem' }} />
                </div>

                {/* Filter and Sort Controls Skeleton */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                    {/* Filter Pills */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <SkeletonElement width="3.5rem" height="2rem" borderRadius="var(--radius-full)" />
                        <SkeletonElement width="4.5rem" height="2rem" borderRadius="var(--radius-full)" />
                        <SkeletonElement width="4.5rem" height="2rem" borderRadius="var(--radius-full)" />
                    </div>
                    {/* Sort Dropdown */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <SkeletonElement width="3rem" height="1rem" />
                        <SkeletonElement width="6rem" height="2rem" borderRadius="var(--radius-md)" />
                    </div>
                </div>

                {/* Poll Cards Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: '1.5rem'
                }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="dashboard-poll-card skeleton-poll-card" style={{
                            background: 'var(--bg-secondary)',
                            // padding/borderRadius handled by CSS class .dashboard-poll-card
                            border: '1px solid var(--border-color)',
                            minHeight: '100px', // Further reduced height for very compact look
                            animationDelay: `${i * 0.1}s`,
                            animationFillMode: 'both',
                            animation: 'fadeInUp 0.4s ease-out',
                            padding: '1rem' // Reduced padding
                        }}>
                            {/* Status Badge */}
                            <SkeletonElement width="4rem" height="1.25rem" borderRadius="var(--radius-full)" style={{ marginBottom: '0.75rem' }} />

                            {/* Title Area - Reduced to single line for compact view */}
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <SkeletonElement width="70%" height="1.5rem" />
                            </div>

                            {/* Footer */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                paddingTop: '0.75rem',
                                borderTop: '1px solid var(--border-color)'
                            }}>
                                {/* Votes */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <SkeletonElement width="1rem" height="1rem" borderRadius="var(--radius-sm)" />
                                    <SkeletonElement width="2rem" height="0.8rem" />
                                </div>
                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <SkeletonElement width="1.75rem" height="1.75rem" borderRadius="var(--radius-md)" />
                                    <SkeletonElement width="1.75rem" height="1.75rem" borderRadius="var(--radius-md)" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

// Main export with variants
function SkeletonLoader({ variant = 'default' }) {
    switch (variant) {
        case 'dashboard':
            return <DashboardSkeleton />
        case 'poll-page':
            return <PollPageSkeleton />
        case 'review-page':
            return <ReviewPageSkeleton />
        case 'admin-page':
            return <AdminPageSkeleton />
        default:
            return (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <SkeletonElement width="60%" height="1.5rem" style={{ margin: '0 auto 1rem' }} />
                    <SkeletonElement width="40%" height="1rem" style={{ margin: '0 auto' }} />
                </div>
            )
    }
}

export { SkeletonElement, SkeletonChoice, PollPageSkeleton, ReviewPageSkeleton, AdminPageSkeleton, DashboardSkeleton }
export default SkeletonLoader
